import axios from 'axios';
import axiosRetry from 'axios-retry';

import _ from 'lodash';
import { useAuthStore } from '@/stores/auth';
import { terminologyUrl } from '@/common/api/terminologyRequest';

// Terminology source JSON files
import administrativeGenderResponseJsonFile from '@/assets/json/administrativeGender.json';
import ethnicityResponseJsonFile from '@/assets/json/ethnicity.json';
import ethnicityDetailResponseJsonFile from '@/assets/json/ethnicityDetail.json';
import genderIdentityResponseJsonFile from '@/assets/json/genderIdentity.json';
import maritalStatusResponseJsonFile from '@/assets/json/maritalStatus.json';
import languagesResponseJsonFile from '@/assets/json/languages.json';
import pronounsResponseJsonFile from '@/assets/json/pronouns.json';
import raceResponseJsonFile from '@/assets/json/race.json';
import relationshipTypesResponseJsonFile from '@/assets/json/relationshipTypes.json';
import relationshipTypesInverse from '@/assets/json/relationshipTypesInverse.json';
import religiousAffiliationResponseJsonFile from '@/assets/json/religiousAffiliation.json';
import sexualOrientationResponseJsonFile from '@/assets/json/sexualOrientation.json';
import familyMemberTypesResponseJsonFile from '@/assets/json/familyMemberTypes.json';

export const $httpTerminology = axios.create({
    baseURL: terminologyUrl(),
    headers: {
        post: { Accept: 'application/json' },
        // cache terminology requests for 2 weeks, this shouldn't change much and doesn't contain ePHI
        get: { Accept: 'application/json', 'cache-control': 'max-age=1209600,private' },
        put: { Accept: 'application/json' },
        patch: { Accept: 'application/json' },
    },
});

if (import.meta.env.VITE_TERMINOLOGY_LOCAL) {
    // Add a request interceptor
    $httpTerminology.interceptors.request.use(
        (config) => {
            const authStore = useAuthStore();
            if (authStore.token) {
                config.headers.setAuthorization('Bearer ' + authStore.token);
            }

            config.baseURL = import.meta.env.VITE_PROTOCOL + _.get(authStore, 'account.fhirApiUri', '');

            return config;
        },
        (err) => {
            // Do something with request error
            return Promise.reject(err);
        },
    );
}

axiosRetry($httpTerminology, { retries: 3 });

export const VALUE_SET_BASE = 'http://fhir.bestnotes.com/r4/ValueSet/';
export const VALUE_SETS = {
    icd10: VALUE_SET_BASE + 'icd-10-cm-all',
    dsm5: VALUE_SET_BASE + 'dsm5',
    dsm5OtherConditions: VALUE_SET_BASE + 'dsm5-other-conditions',
    cpt: VALUE_SET_BASE + 'cpt-codes',
    rxnormAllergens: VALUE_SET_BASE + 'rxnorm-allergens',
    rxnormClinicalDrugs: VALUE_SET_BASE + 'rxnorm-clinical-drugs',
    identifierTypesProvider: VALUE_SET_BASE + 'identifier-types-provider',
    identifierTypesLocation: VALUE_SET_BASE + 'identifier-types-location',
    identifierTypesPayer: VALUE_SET_BASE + 'identifier-types-payer',
    paymentTypesInsurance: VALUE_SET_BASE + 'payment-types-insurance',
    careTeamRoles: VALUE_SET_BASE + 'care-team-roles',
};

const PROPERTY_EXTENSION = 'http://hl7.org/fhir/5.0/StructureDefinition/extension-ValueSet.expansion.contains.property';
const PAGE_SIZE = 1000;
const fullListCache = new Map();

// Properties arrive as R4 cross-version extensions; the R5 `property` element is read too.
function readProperties(item) {
    const properties = {};
    const fromExtension = (item.extension || [])
        .filter((ext) => ext.url === PROPERTY_EXTENSION)
        .map((ext) => {
            const code = ext.extension?.find((e) => e.url === 'code')?.valueCode;
            const value = ext.extension?.find((e) => e.url === 'value');
            return { code, value };
        });
    const fromElement = (item.property || []).map((prop) => ({ code: prop.code, value: prop }));
    for (const { code, value } of [...fromExtension, ...fromElement]) {
        if (!code || !value) continue;
        const key = Object.keys(value).find((k) => k.startsWith('value'));
        if (key) properties[code] = value[key];
    }
    return properties;
}

function normalize(item) {
    const concept = { system: item.system, code: item.code, display: item.display, properties: readProperties(item) };
    if (item.definition) concept.definition = item.definition;
    return concept;
}

function flatten(contains = []) {
    return contains.flatMap((item) => [item, ...flatten(item.contains)]);
}

function includeFor(system) {
    return typeof system === 'string' ? { system } : system;
}

async function expandPage({ url, systems, filter, count, offset, properties, activeOnly }) {
    // Aidbox drops every property from a GET that names more than one, so those POST.
    if (url && !properties?.length) {
        const params = { url, count, offset };
        if (filter) params.filter = filter;
        if (activeOnly) params.activeOnly = true;
        const response = await $httpTerminology.get('fhir/ValueSet/$expand', { params });
        return response.data.expansion;
    }

    const valueSet = url ? { name: 'url', valueUri: url } : { name: 'valueSet', resource: { resourceType: 'ValueSet', status: 'active', compose: { include: systems.map(includeFor) } } };
    const parameter = [valueSet, { name: 'count', valueInteger: count }, { name: 'offset', valueInteger: offset }];
    if (filter) parameter.push({ name: 'filter', valueString: filter });
    if (activeOnly) parameter.push({ name: 'activeOnly', valueBoolean: true });
    for (const property of properties || []) parameter.push({ name: 'property', valueString: property });
    const response = await $httpTerminology.post('fhir/ValueSet/$expand', { resourceType: 'Parameters', parameter });
    return response.data.expansion;
}

/**
 * Expands a named ValueSet (`url`) or an inline one over `systems` (a system url, or a
 * compose.include object), returning `{ system, code, display, definition?, properties }`.
 * `all` pages through the whole expansion and is cached for the session. Codes without a
 * display are hierarchy parents or unnamed entries, and are dropped. Throws on failure.
 */
export async function expandValueSet({ url, systems, filter, count = 100, properties, activeOnly, all = false } = {}) {
    if (!url && !systems?.length) throw new Error('expandValueSet needs a url or systems');

    const key = all ? JSON.stringify({ url, systems, filter, properties, activeOnly }) : null;
    if (key && fullListCache.has(key)) return fullListCache.get(key);

    const request = (async () => {
        const items = [];
        let offset = 0;
        for (;;) {
            const expansion = await expandPage({ url, systems, filter, count: all ? PAGE_SIZE : count, offset, properties, activeOnly });
            const page = flatten(expansion?.contains);
            items.push(...page);
            offset += page.length;
            if (!all || !page.length || offset >= (expansion?.total ?? 0)) break;
        }
        return items.filter((item) => item.display).map(normalize);
    })();

    if (key) {
        fullListCache.set(key, request);
        request.catch(() => fullListCache.delete(key));
    }
    return request;
}

export function clearTerminologyCache() {
    fullListCache.clear();
}

export function searchIcd10(term, count = 100) {
    return expandValueSet({ url: VALUE_SETS.icd10, filter: term, count });
}

export function searchDsm5(term, count = 100) {
    return expandValueSet({ url: VALUE_SETS.dsm5, filter: term, count, properties: ['endDate', 'disorderId', 'icd10'] });
}

export function searchCpt(term, count = 100) {
    return expandValueSet({ url: VALUE_SETS.cpt, filter: term, count });
}

export function searchAllergen(term, count = 100) {
    return expandValueSet({ url: VALUE_SETS.rxnormAllergens, filter: term, count });
}

/**
 * Clinical drugs carry the strength and dose form ScriptSure matches on. A concept with a
 * has_quantified_form property is superseded by its quantified form and is left out.
 */
export async function searchClinicalDrug(term, count = 100) {
    const results = await expandValueSet({ url: VALUE_SETS.rxnormClinicalDrugs, filter: term, count, activeOnly: true, properties: ['has_quantified_form'] });
    return results.filter((concept) => !('has_quantified_form' in concept.properties));
}

export async function administrativeGender() {
    return administrativeGenderResponseJsonFile.data.entry;
}

export async function religiousAffiliation() {
    return religiousAffiliationResponseJsonFile.data.entry;
}

export async function ethnicity() {
    return ethnicityResponseJsonFile.data.entry;
}

export async function ethnicityDetail() {
    return ethnicityDetailResponseJsonFile.data.entry;
}

export async function race() {
    return raceResponseJsonFile.data.entry;
}

export async function sexualOrientation() {
    return sexualOrientationResponseJsonFile.data.entry;
}

export async function genderIdentity() {
    return genderIdentityResponseJsonFile.data.entry;
}

export async function maritalStatus() {
    return maritalStatusResponseJsonFile.data.entry;
}

export async function pronouns() {
    return pronounsResponseJsonFile.data.entry;
}

export async function languages() {
    return languagesResponseJsonFile.data.entry;
}

export async function getGenderDisplay(gender) {
    let genderResources = await administrativeGender();
    return genderResources.map((g) => g.resource).find((o) => o.code === gender)?.display;
}

export async function getRelationshipTypes() {
    return relationshipTypesResponseJsonFile.data.entry;
}

export async function getFamilyMemberTypes() {
    return familyMemberTypesResponseJsonFile.data.entry;
}

export async function getRelationshipTypesInverse() {
    return relationshipTypesInverse;
}

export function findOrganizationRelationshipList(thisContact, relatedContact) {
    if (thisContact === 'Organization' && relatedContact !== 'Organization') {
        return [
            { display: 'Contractor', code: 'BN_CONTRACTOR', definition: 'Related Person is contractor to organization' },
            { display: 'Employee', code: 'BN_EMPLOYEE', definition: 'Related Person is employee to organization' },
            { display: 'Other', code: 'BN_OTHER', definition: 'Related Person is other relationship to organization' },
        ];
    }

    if (thisContact !== 'Organization' && relatedContact === 'Organization') {
        const authStore = useAuthStore();
        return [
            // Employer is also used in the normal terminology service so leaving this relationship alone
            { display: 'Employer', code: 'E', definition: 'Related Person is employer to ' + authStore.clientVocab.toLowerCase() },
            { display: 'Provider', code: 'BN_PROVIDER', definition: 'Related Person is provider to ' + authStore.clientVocab.toLowerCase() },
            { display: 'Other', code: 'BN_OTHER', definition: 'Related Person is other relationship to ' + authStore.clientVocab.toLowerCase() },
        ];
    }

    if (thisContact === 'Organization' && relatedContact === 'Organization') {
        return [
            { display: 'Business Associate', code: 'BN_BUSINESS_ASSOCIATE', definition: 'Related Organization has a business relationship to organization' },
            { display: 'Parent Company', code: 'BN_PARENT_COMPANY', definition: 'Related Organization is a parent company to organization' },
            { display: 'Subsidiary', code: 'BN_SUBSIDIARY', definition: 'Related Organization is a subsidiary to this organization' },
            { display: 'Other', code: 'BN_OTHER', definition: 'Related Person is other relationship to organization' },
        ];
    }
}
// todo: configure terminology server for jwt
// Add a request interceptor
// $httpTerminology.interceptors.request.use(
//     config => {
//         if (authStore.token) {
//             config.headers.common['Authorization'] = 'Bearer ' + authStore.token;
//         }
//         return config;
//     },
//     err => {
//         // Do something with request error
//         return Promise.reject(err);
//     },
// );
